import type { UserLocationHistory } from 'api/models/api';
import type {
  ColorPalette,
  PublicCalendarCodeResult,
  User,
  UserAppearanceToEdit,
  UserDetail,
  UserGroupsToManage,
  UserSkillsToManage,
  UserToAdd,
  UserToEdit
} from 'api/models/mainservice';
import { protectedApiClient, protectedApiClientV2 } from 'api/support/baseApiClients';

export default {
  addUser: async (user: UserToAdd) => {
    const result = await protectedApiClient.post<UserDetail>(`/User`, user);
    return result.data;
  },
  deleteUser: async (userId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/User/${userId}`);
    return result.data;
  },
  deleteUserDevice: async (params: { userId: string; deviceId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/User/${params.userId}/device/${params.deviceId}`
    );
    return result.data;
  },
  disableUser: async (userId: string) => {
    const result = await protectedApiClient.put<undefined>(`/User/${userId}/disable`);
    return result.data;
  },
  disableUserDevice: async (params: { userId: string; deviceId: string }) => {
    const result = await protectedApiClient.put<undefined>(
      `/User/${params.userId}/device/${params.deviceId}/disable`
    );
    return result.data;
  },
  editUser: async (params: { userId: string; values: UserToEdit }) => {
    const result = await protectedApiClient.put<UserDetail>(
      `/User/${params.userId}`,
      params.values
    );
    return result.data;
  },
  enableUser: async (userId: string) => {
    const result = await protectedApiClient.put<undefined>(`/User/${userId}/enable`);
    return result.data;
  },
  enableUserDevice: async (params: { userId: string; deviceId: string }) => {
    const result = await protectedApiClient.put<undefined>(
      `/User/${params.userId}/device/${params.deviceId}/enable`
    );
    return result.data;
  },
  getUsers: async () => {
    const result = await protectedApiClient.get<User[]>(`/User`);
    return result.data;
  },
  getUserLocationHistory: async (userId: string, selectedDay: string) => {
    const result = await protectedApiClientV2.get<UserLocationHistory>(
      `/users/${userId}/locations/${selectedDay}`
    );
    return result.data;
  },
  getUserDetail: async (userId: string) => {
    const result = await protectedApiClient.get<UserDetail>(`/User/${userId}`);
    return result.data;
  },
  changeUserPassword: async (userId: string, newPassword: string) => {
    const result = await protectedApiClient.put<undefined>(`/User/${userId}/ChangePassword`, {
      newPassword
    });
    return result.data;
  },
  enableUserPublicCalendar: async (userId: string) => {
    const result = await protectedApiClient.post<PublicCalendarCodeResult>(
      `/user/${userId}/publicCalendar`
    );
    return result.data;
  },
  disableUserPublicCalendar: async (userId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/user/${userId}/publicCalendar`);
    return result.data;
  },
  resetUserPublicCalendar: async (userId: string) => {
    const result = await protectedApiClient.put<PublicCalendarCodeResult>(
      `/user/${userId}/publicCalendar/reset`
    );
    return result.data;
  },
  manageGroups: async (params: { userId: string; values: UserGroupsToManage }) => {
    const result = await protectedApiClient.put<UserDetail>(
      `/user/${params.userId}/groups`,
      params.values
    );

    return result.data;
  },
  manageSkills: async (params: { userId: string; values: UserSkillsToManage }) => {
    const result = await protectedApiClient.put<UserDetail>(
      `/user/${params.userId}/skills`,
      params.values
    );

    return result.data;
  },
  nextAvailableColor: async () => {
    const result = await protectedApiClient.get<ColorPalette>('/user/nextColorPalette');

    return result.data;
  },
  deleteUserProfilePicture: async (userId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/user/${userId}/profilePicture`);
    return result.data;
  },
  changeUserAppearance: async (userId: string, appearance: UserAppearanceToEdit) => {
    const result = await protectedApiClient.put<undefined>(
      `/user/${userId}/appearance`,
      appearance
    );
    return result.data;
  }
};
